/// <summary>
/// Adjust归因逻辑
/// </summary>

import {AdjustAttr} from "./AdjustAttr";
import {KitEvent} from "../Track/KitEvent";
import {KitSp} from "../KitSp";
import KitLog from "../KitLog";
import KitAPI from "../KitAPI";
import KitCfg from "../KitCfg";
import {EventName, RecodeKey} from "@/Fwk/Kit/Track/TrackConst";

/// <author>xiexingwu</author>
export abstract class AdjustBase {
    private static PREFS_KEY = "adjust_";
    private static READY = "ready";
    protected static ORGANIC = "organic";
    private static NETWORK = "network";
    private static CAMPAIGN = "campaign";
    private static ADGROUP = "adgroup";
    private static CREATIVE = "creative";
    private static CLICKLABEL = "clicklabel";
    private static USER_BUY_FLAG = "user_buy_flag";
    private static ADID = "adid";

    private static ATTR_URL = "attr_url";
    private static GOOGLEAID = "googleaid";
    private static CONSUME_TIME = "consume_time";

    private _network = "";
    protected _adid = "";
    private _creative = "";
    private _campaign = "";
    private _adgroup = "";
    private _clickLabel = "";
    private _isBuyUser = false;
    protected startTime: number = 0;
    private attributed = false;

    private readonly listeners: (() => void)[] = [];

    AddListener(action: () => void) {
        const index = this.listeners.indexOf(action);
        if (index < 0) this.listeners.push(action);
    }

    RemoveListener(action: () => void) {
        const index = this.listeners.indexOf(action);
        if (index >= 0) delete this.listeners[index];
    }

    abstract Init(onChangedAction: () => void): void;

    abstract GetAdjustAttribution(): void;

    abstract AdjustEvent(token: string, dic: Map<string, string>): void;

    abstract trackAdjustEvent(token: string, revenue: number): void;

    protected OnAttr(attr: AdjustAttr) {
        if (attr == null) return;

        const oldNetwork = this.GetDistributor(false);

        const network = attr.network;
        const adid = attr.adid;
        const creative = attr.creative;
        const campaign = attr.campaign;
        const adgroup = attr.adgroup;
        const clickLabel = attr.clickLabel;
        let trackerToken = attr.trackerToken;
        let trackerName = attr.trackerName;
        let costType = attr.costType;
        const costAmount = attr.costAmount;
        let costCurrency = attr.costCurrency;
        let fbInstallReferrer = attr.fbInstallReferrer;

        this._network = (!network) ? AdjustBase.ORGANIC : network;
        if (this._network.endsWith("_int")) this._network = this._network.replace("_int", "");
        this._adid = (!adid) ? "" : adid;
        this._creative = (!creative) ? "" : creative;
        this._campaign = (!campaign) ? "" : campaign;
        this._adgroup = (!adgroup) ? "" : adgroup;
        this._clickLabel = (!clickLabel) ? "" : clickLabel;

        trackerToken = (!trackerToken) ? "" : trackerToken;
        trackerName = (!trackerName) ? "" : trackerName;
        costType = (!costType) ? "" : costType;
        costCurrency = (!costCurrency) ? "" : costCurrency;
        fbInstallReferrer = (!fbInstallReferrer) ? "" : fbInstallReferrer;

        this._isBuyUser = !(!this._network) && !this._network.toLowerCase().concat(AdjustBase.ORGANIC);

        const attrFormat =
            `tt:${trackerToken} tn:${trackerName} net:${this._network} cam:${this._campaign} adg:${this._adgroup} cre:${this._creative} cl:${this._clickLabel} adid:${this._adid} ct:${costType} ca:${costAmount.toFixed(2).replace(/(\.\d*[1-9])0+$|\.0+$/, '$1')} cc:${costCurrency} fir:${fbInstallReferrer}`;

        KitSp.SetString(AdjustBase.PREFS_KEY + AdjustBase.NETWORK, this._network);
        KitSp.SetString(AdjustBase.PREFS_KEY + AdjustBase.ADID, this._adid);
        KitSp.SetString(AdjustBase.PREFS_KEY + AdjustBase.CREATIVE, this._creative);
        KitSp.SetString(AdjustBase.PREFS_KEY + AdjustBase.CAMPAIGN, this._campaign);
        KitSp.SetString(AdjustBase.PREFS_KEY + AdjustBase.ADGROUP, this._adgroup);
        KitSp.SetString(AdjustBase.PREFS_KEY + AdjustBase.CLICKLABEL, this._clickLabel);
        KitSp.SetInt(AdjustBase.PREFS_KEY + AdjustBase.USER_BUY_FLAG, this._isBuyUser ? 1 : 0);
        KitSp.SetInt(AdjustBase.PREFS_KEY + AdjustBase.READY, 1);
        KitSp.Apply();

        this.attributed = true;

        //买量信息回调
        if (KitLog.IsLog) KitLog.Log(`[Adjust] 归因回调:${attrFormat}`);

        this.recodeActiveBuy(attrFormat);
        for (let listener of this.listeners) {
            listener();
        }

        if (oldNetwork != this._network) {
            //渠道发生变化
            // KitGame.OnAdjustChange();
        }
    }

    public recodeActiveBuy(attrFormat: string) {
        const totalMills = new Date().getTime() - this.startTime;

        KitEvent.track113(EventName.ACTIVE_BUY)
            .Put(RecodeKey.ATTRIBUTION_ID, this._adid)
            .Put(RecodeKey.ATTR_URL, attrFormat)
            .Put(RecodeKey.GOOGLEAID, KitAPI.ADID)
            .Put(RecodeKey.CONSUME_TIME, totalMills).Commit();

        const map = new Map<string, string>();
        map.set(AdjustBase.ATTR_URL, attrFormat);
        map.set(AdjustBase.GOOGLEAID, KitAPI.ADID);
        map.set(AdjustBase.CONSUME_TIME, totalMills.toString());
        this.AdjustEvent(this.GetAdJustBuyEventToken(), map);

        KitAPI.GoogleGTMEvent(EventName.ACTIVE_BUY, "");
    }

    IsReady(): boolean {
        return this.attributed || KitSp.GetInt(AdjustBase.PREFS_KEY + AdjustBase.READY, 0) == 1;
    }

    GetDistributor(canAccessAdjust: boolean = true): string {
        if (this.attributed) return this._network;
        return KitSp.GetString(AdjustBase.PREFS_KEY + AdjustBase.NETWORK, AdjustBase.ORGANIC);
    }

    public GetSecondDistributor(): string {
        if (this.attributed) return this._campaign;
        return KitSp.GetString(AdjustBase.PREFS_KEY + AdjustBase.CAMPAIGN, this._campaign);
    }

    public GetAdGroup(): string {
        if (this.attributed) return this._adgroup;
        return KitSp.GetString(AdjustBase.PREFS_KEY + AdjustBase.ADGROUP, this._adgroup);
    }

    public IsBuyUserFlag(): boolean {
        if (this.attributed) return this._isBuyUser;
        return KitSp.GetInt(AdjustBase.PREFS_KEY + AdjustBase.USER_BUY_FLAG, 0) == 1;
    }

    public GetClickLabel(): string {
        if (this.attributed) return this._clickLabel;
        return KitSp.GetString(AdjustBase.PREFS_KEY + AdjustBase.CLICKLABEL, this._clickLabel);
    }

    public GetCreative(): string {
        if (this.attributed) return this._creative;
        return KitSp.GetString(AdjustBase.PREFS_KEY + AdjustBase.CREATIVE, this._creative);
    }

    public GetAdjustAdid(): string {
        if (this.attributed) return this._adid;
        return KitSp.GetString(AdjustBase.PREFS_KEY + AdjustBase.ADID, this._adid);
    }

    public GetAdjustToken(): string {
        return KitCfg.adjust_app_token;
    }

    public GetAdJustBuyEventToken(): string {
        return KitCfg.adjust_event_token;
    }
}
